<template>
	<div>
		<TaskView 
			:color="$attrs.color"
			:statusesInit="statusesInit"
			:customiseInit="customiseInit"
			@create="onCreate($event)"
			@new-search="onNewSearch()"
			@switch="onListSwitch()"
			@status="onStatusChange($event)"
			@customise-view="onCustomiseView($event)"
		/>
		<v-alert class="mx-6 mt-3" type="error" v-if="error_msg.length !== 0">
			{{ error_msg }}
		</v-alert>
		<ProgressLinear v-if="loading"/>
		<v-row v-if="!loading" class="ma-0">
			<v-col v-if="isSearch" :cols="$vuetify.breakpoint.smAndDown ? 12:3">
				<TaskSearch
					v-if="isSearch"
					:statuses="statusses"
					:color="$attrs.color"
					:id="search_item ? search_item.id : 0"
					:title="search_item ? search_item.name : 'Search Tasks'"
					:settings="search_item ? search_item.settings : null"
					@error-msg="error_msg = $event"
					@close="onCloseSearch()"
					@search="onSearch($event)"
				/>
			</v-col>
			<v-col :cols="isSearch ? ($vuetify.breakpoint.smAndDown ? 12: 9 ): 12">
				<TaskList
					:key="`tasklist_${tasklist_key}`"
					:color="$attrs.color" 
					:statuses="statusses" 
					:headers="task_headers"
					:statusesFiltered="statusesFiltered"
					:perpage="perpage"
					:search_param="search_param"
					:isSaveAll="isSaveAll"
					@perpage="onPerpageChange($event)"
					@search="onShowSearch($event)"
					@current_unsaved="current_unsaved_ids = $event"
				/>
			</v-col>
		</v-row>
		<TaskUnsavedDialog 
			v-if="unsaved_create || unsaved_switch" 
			:visible="unsaved_create || unsaved_switch" 
			:type="`warning`" 
			:title_text="`Unsaved changes in Tasklist`" 
			:content_text="`Do you want to save changes to this Tasklist?`"
			:confirm_button="`Save changes`"
			:cancel_button="`Don't save`"
			:icon_name="`mdi-check`"
			:maxWidth="500"
			@cancel="onTaskUnsavedCancel()"
			@ok="isSaveAll = true"
		/>
		<CreateEventDialog
			v-if="isCreate"
			:visible="isCreate"
			:createObj="create_obj"
			:color="$attrs.color"
			@close="create_obj = []; isCreate = false"
		/>
		<v-fab-transition v-if="$vuetify.breakpoint.smAndDown">
			<v-btn
				class="create-btn"
				:color="$attrs.color"
				fab
				dark
				small
				bottom
				right
				@click="onCreate(null)"
			>
				<v-icon>mdi-plus</v-icon>
			</v-btn>
		</v-fab-transition>
	</div>
</template>
<script>
import TaskView from "@/views/tasks/task_view.vue";
import ProgressLinear from "@/components/progress/progress_getdata_linear.vue";
import TaskSearch from "@/views/tasks/task_search.vue";
import TaskList from "@/views/tasks/task_list.vue";
import TaskUnsavedDialog from "@/views/tasks/task_unsaved_dialog.vue";
import CreateEventDialog from "@/views/timesheets/create_event_dialog.vue";
import config from "@/config.js";

const TASK_STATUS = [
	{ text: 'Incomplete tasks', value: config.task_incomplete },
	{ text: 'Completed tasks', value: config.task_completed },
	// { text: 'Recurring tasks', value: 'recurring' },
];

const CREATE_ITEMS = config.create_items;

/* the user will be able to create and amend tasks */
const TR = 'TR';
/* the user will be able to enter and amend Items Charges */
const CH = 'CH';
/* If selected the user can manage public holidays */
const DPubHol = 'DPubHol';

const MENU_WINNAME = 'Menu';

export default {
	name: 'Tasks',
	data() {
		return {
			error_msg: '',
			loading: false,
			statusses: [],
			completed_id: 0,
			tasklist_key: 0,

			statusesInit: TASK_STATUS.filter(status => { return status.value !== config.task_completed}),
			customiseInit: config.task_head.filter(head => { return head.showable}),

			statusesFiltered: [],
			task_headers: config.task_head.filter(head => { return head.showable}),	
			perpage: 10,		
			
			isCreate: false,
			create_obj: [],
			isSearch: false,
			search_item: null,
			search_param: null,

			current_unsaved_ids: [],
			unsaved_create: false,
			unsaved_switch: false,
			isSaveAll: false,
		}
	},
	components: {
		TaskView,
		ProgressLinear,
		TaskSearch,
		TaskList,
		TaskUnsavedDialog,
		CreateEventDialog
	},
	async created() {
		await this.getStatusses();

		this.$watch(
			() => this.$route.name,
			() => {
				this.getSettings()
			},
			{ immediate: true }
		)

		this.$store.commit("push_breadcrumb", {
			exact: true,
			text: this.$route.params.id === '0' ? 'Tasks' : `Tasklist ${this.$route.params.id}`,
			to: this.$route.path
		});
	},
	computed: {
		privileges () {
			return this.$store.state.privileges;
		},
	},
	watch: {
		current_unsaved_ids() {
			if (this.current_unsaved_ids.length === 0) {
				this.isSaveAll = false;
				
				this.onCloseSearch();
				if (this.unsaved_create) {
					this.unsaved_create = false;
					this.isCreate = true;
				}
				if (this.unsaved_switch) {
					this.unsaved_switch = false;
					this.$router.push({ name: 'Timesheets'});
				}
			}
		}
	},
	methods: {
		async getStatusses() {
			this.loading = true;
			const url = '/api/tasks/meta';

			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					let json = val.data;

					// For some reason axios isn't always converting our data
					if ((typeof json) == "string") {
						json = JSON.parse(json);
					}

					const { status, meta, reason } = json;
					if (status == "OK") {
						this.statusses = meta.statusses;

						const completed_id_arr = meta.statusses.filter(s => { return s.type === config.status_completed_type });
						this.completed_id = completed_id_arr[0].id;

						const uncompleted_id_arr = meta.statusses.filter(s => { return s.type !== config.status_completed_type });
						this.statusesFiltered = uncompleted_id_arr.map(s => { return s.id });
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				})
				.finally(() => {
					this.loading = false;
				});
		},
		async getSettings() {
			this.loading = true;
			const usid = this.$store.state.user.id;
			const winname_tmp = this.$route.name;
			const winname = config.win_name[`${winname_tmp}`];

			const url = `/api/user/${usid}/webwinsetup/${winname}`;

			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						if (data.length !== 0) {
							const current_settings = data.filter(d => { return d.user_id === usid });
							const settings_winname = current_settings[0].window_name;
							const settings_windata_json = current_settings[0].windata_json;
							if (settings_winname === winname) {
								const windata = JSON.parse(settings_windata_json);

								const type = windata.type;
								if (type) {
									if (type.length === 0) {
										this.statusesInit = [];
									} else if (type.length === 1 && type.includes(this.completed_id)) {
										this.statusesInit = TASK_STATUS.filter(status => { return status.value === config.task_completed});
									} else {
										this.statusesInit = type.includes(this.completed_id) ? TASK_STATUS : TASK_STATUS.filter(status => { return status.value !== config.task_completed});
									}
									this.statusesFiltered = windata.type;
								}

								const options = windata.options;
								if (options) {
									options.forEach(o => {
										const text_arr = config.task_head.filter(h => { return h.value === o.value });
										o['text'] = text_arr[0].text;
									});

									this.customiseInit = options.filter(head => {return head.value !== 'select' && head.value !== 'drag'});
									this.task_headers = options.filter(head => {return head.value !== 'drag'});;
								}

								const perpage = windata.perpage;
								if (perpage) {
									this.perpage = perpage;
								}
							}
						}

						this.loading = false;
					} else {
						this.error_msg = reason;
						this.loading = false;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
					this.loading = false;
				});
		},
		updateSettings(win_setup) {
			const usid = this.$store.state.user.id;
			const winname_tmp = this.$route.name;
			const winname = config.win_name[`${winname_tmp}`];

			const url = `/api/user/${usid}/webwinsetup/${winname}`;

			const params_obj = {};
			params_obj['value'] = win_setup;

			return new Promise((resolve, reject) => {
				axios
					.post(url, params_obj, {
						headers: {
							"Authorization": "Bearer " + this.$store.state.token,
							"Accept": "application/json"
						}
					})
					.then((val) => {
						const json = val.data;

						const { status, data, reason } = json;

						if (status == "OK") {
							resolve(data);
						} else {
							reject(reason);
						}
					})
					.catch((e) => {
						reject(e.message);
					});
			});			
		},
		onCreate(create_obj) {
			if (create_obj) {
				this.create_obj = create_obj;
			} else {
				let create_items = CREATE_ITEMS;
				if (!this.privileges.includes(TR)) {
					create_items = create_items.filter(c => {return c.value !== 'task'});
				}
				if (!this.privileges.includes(CH)) {
					create_items = create_items.filter(c => {return c.value !== 'item'});
				}
				if (!this.privileges.includes(DPubHol)) {
					create_items = create_items.filter(c => {return c.value !== 'holiday'});
				}
				this.create_obj = create_items;
			}
			

			if (this.current_unsaved_ids.length === 0) {
				this.isCreate = true;
			} else {
				this.unsaved_create = true;
			}			
		},
		onListSwitch() {
			if (this.current_unsaved_ids.length === 0) {
				const to_router = '/tasks/timesheets';
				const win_setup_obj = { to: to_router };
				const win_setup = JSON.stringify(win_setup_obj);

				const usid = this.$store.state.user.id;
				const winname_tmp = MENU_WINNAME;
				const winname = config.win_name[`${winname_tmp}`];

				const url = `/api/user/${usid}/webwinsetup/${winname}`;
				const params_obj = {};
				params_obj['value'] = win_setup;

				axios
					.post(url, params_obj, {
						headers: {
							"Authorization": "Bearer " + this.$store.state.token,
							"Accept": "application/json"
						}
					})
					.then((val) => {
						const json = val.data;

						const { status, reason } = json;

						if (status == "OK") {
						} else {
							console.log(reason)
						}
					})
					.catch((e) => {
						console.log(e.message)
					});	

				this.$router.push({ name: 'Timesheets'});
			} else {
				this.unsaved_switch = true;
			}
		},
		onStatusChange(status) {
			const statusesFiltered_tmp = [];

			status.forEach(s => {
				if (s.value === config.task_incomplete) {
					this.statusses.forEach(status => {
						if (status.type !== config.status_completed_type) {
							statusesFiltered_tmp.push(status.id);
						}
					});
				}
				if (s.value === config.task_completed) {
					this.statusses.forEach(status => {
						if (status.type === config.status_completed_type) {
							statusesFiltered_tmp.push(status.id);
						}
					});
				}
			});

			this.statusesFiltered = statusesFiltered_tmp;

			// update the settings if status changing
			const win_setup_obj = {
				type: this.statusesFiltered,
				options: this.task_headers,
				perpage: this.perpage
			};
			
			const win_setup_str = JSON.stringify(win_setup_obj);

			this.updateSettings(win_setup_str)
				.then(() => {
				})
				.catch((reason) => {
					this.error_msg = reason;
				});
		},
		onCustomiseView(column) {
			let task_headers_tmp = column;

			const column_value_arr = column.map(c => { return c.value});
			if (!column_value_arr.includes('select') || !column_value_arr.includes('drag')) {
				const head_select = config.task_head.filter(h => { return h.value === 'select' || h.value === 'drag' });
				const task_headers_no_select_drag = column.filter(h => { return h.value !== 'select' && h.value !== 'drag'})

				task_headers_tmp = [...head_select, ...task_headers_no_select_drag];
			}
			
			this.task_headers = task_headers_tmp.filter(head => { return head.showable === true });

			// update the settings if CustomiseView changing
			const win_setup_obj = {
				type: this.statusesFiltered,
				options: task_headers_tmp,
				perpage: this.perpage
			};
			
			const win_setup_str = JSON.stringify(win_setup_obj);

			this.updateSettings(win_setup_str)
				.then(() => {
				})
				.catch((reason) => {
					this.error_msg = reason;
				});
		},
		onPerpageChange(perpage) {
			this.perpage = perpage;

			// update the settings if PerPage changing
			const win_setup_obj = {
				type: this.statusesFiltered,
				options: this.task_headers,
				perpage: this.perpage
			};
			
			const win_setup_str = JSON.stringify(win_setup_obj);

			this.updateSettings(win_setup_str)
				.then(() => {
				})
				.catch((reason) => {
					this.error_msg = reason;
				});
		},
		onNewSearch() {
			this.search_item = null;
			this.search_param = null;
			this.isSearch = true;
		},
		onShowSearch(item) {
			const item_tmp = {};
			for (const [key, value] of Object.entries(item)) {
				if (key === 'settings') {
					if (typeof(value) === 'string') {
						item_tmp[`${key}`] = JSON.parse(value);
					}
					if (typeof(value) === 'object') {
						item_tmp[`${key}`] = value;
					}
				} else {
					item_tmp[`${key}`] = value;
				}
			}

			this.search_item = item_tmp;
			this.search_param = null;
			if (!this.isSearch) {
				this.isSearch = true;
			}
		},
		onSearch(param) {
			this.search_param = param;
		},
		onCloseSearch() {
			this.isSearch = false;
			this.search_item = null;
			this.search_param = null;
		},
		onTaskUnsavedCancel() {
			if (this.unsaved_create) {
				this.current_unsaved_ids = [];
				this.tasklist_key += 1;
				this.isCreate = true;
			}
			if (this.unsaved_switch) {
				this.current_unsaved_ids = [];
			}
		},
	}
}
</script>
<style lang="scss" scoped>
.create-btn {
	position: fixed;
	bottom: 16px;
	right: 16px;
	z-index: 14;
}
</style>